import { forwardRef } from "react";
import { Link, NavLink, useParams, type LinkProps, type NavLinkProps } from "react-router-dom";
import { isSupportedLocale, pickDefaultLocale, withLocale } from "@/i18n/locale-utils";
import type { SupportedLanguage } from "@/i18n";

function useCurrentLocale(): SupportedLanguage {
  const { locale } = useParams<{ locale: string }>();
  if (isSupportedLocale(locale)) return locale;
  return pickDefaultLocale();
}

function shouldSkipLocalization(to: LinkProps["to"]): boolean {
  if (typeof to !== "string") return false;
  if (to.startsWith("http://") || to.startsWith("https://") || to.startsWith("//")) return true;
  if (to.startsWith("mailto:") || to.startsWith("tel:")) return true;
  if (to.startsWith("#")) return true;
  if (!to.startsWith("/")) return true; // relative path — leave alone
  return false;
}

function localizeTo(to: LinkProps["to"], locale: SupportedLanguage): LinkProps["to"] {
  if (typeof to === "string") {
    if (shouldSkipLocalization(to)) return to;
    return withLocale(locale, to);
  }
  // To object form
  if (to && typeof to === "object" && "pathname" in to && to.pathname) {
    if (shouldSkipLocalization(to.pathname)) return to;
    return { ...to, pathname: withLocale(locale, to.pathname) };
  }
  return to;
}

export const LocalizedLink = forwardRef<HTMLAnchorElement, LinkProps>(function LocalizedLink(
  { to, ...rest },
  ref,
) {
  const locale = useCurrentLocale();
  return <Link ref={ref} to={localizeTo(to, locale)} {...rest} />;
});

export const LocalizedNavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(function LocalizedNavLink(
  { to, ...rest },
  ref,
) {
  const locale = useCurrentLocale();
  return <NavLink ref={ref} to={localizeTo(to, locale)} {...rest} />;
});
